import React, { useEffect } from 'react';
import { get, isEqual } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import {
  Base,
  Button,
  Placeholder,
  Select,
  toast
} from 'src/components';
import { updateCompanyQuery, useCompany } from 'src/queries/company';
import COMMON_CONSTANTS from 'common/commonConstants';
import STYLE from 'src/constants/style';

const { DEFAULT_DATE_FILTER_OPTIONS } = COMMON_CONSTANTS;

const getDefaultDateFilterLabel = (id) => {
  const defaultOption = DEFAULT_DATE_FILTER_OPTIONS.find((op) => id === op.id);
  return defaultOption ? defaultOption.label : false;
};

const getDefaultDateFilterId = (label) => {
  const defaultOption = DEFAULT_DATE_FILTER_OPTIONS.find((op) => label === op.label);
  return defaultOption ? defaultOption.id : false;
};

const isSaveDisabled = (
  company,
  newDefaultDateFilterId,
  isCompanyFetching,
  isUpdateCompanyLoading
) => {
  const initialDefaultDateOption = get(
    company,
    'settings.defaultDateFilter'
  );
  const newDefaultDateFilterOption = getDefaultDateFilterLabel(newDefaultDateFilterId);
  const isDefaultOptionUnchanged = !newDefaultDateFilterOption
    || isEqual(initialDefaultDateOption, newDefaultDateFilterOption);
  if (
    isCompanyFetching
    || isUpdateCompanyLoading
    || isDefaultOptionUnchanged
  ) {
    return true;
  }

  return false;
};

const DefaultDateFilter = () => {
  const {
    data: company,
    refetch: refetchCompany,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany();

  const {
    mutateAsync: updateCompany,
    isLoading: isUpdateCompanyLoading
  } = updateCompanyQuery();

  const {
    handleSubmit, control, reset, watch
  } = useForm({});

  useEffect(() => {
    const defaultOption = get(
      company,
      'settings.defaultDateFilter'
    );

    if (defaultOption) {
      reset({
        defaultDateFilter: getDefaultDateFilterId(defaultOption)
      });
    }
  }, [company?.settings?.defaultDateFilter]);

  const { defaultDateFilter } = watch();

  const isFetching = isFetchingCompany;
  const isError = isErrorCompany;
  const isReady = company
    && company.id
    && !isFetching
    && !isError
    && !isUpdateCompanyLoading;

  if (!isReady) {
    return <Placeholder />;
  }

  const save = async (data) => {
    toast.show('Updating default dashboards date filter');
    const newDefaultDateFilter = getDefaultDateFilterLabel(data.defaultDateFilter);
    const res = await updateCompany({
      settings: {
        ...company.settings,
        defaultDateFilter: newDefaultDateFilter
      }
    });

    if (!res.success) {
      toast.error(res.message);
      return;
    }

    toast.show('Updated!');
    refetchCompany();
  };

  const isUpdateButtonDisabled = isSaveDisabled(
    company,
    defaultDateFilter,
    isFetching,
    isUpdateCompanyLoading
  );

  return (
    <Base classes={STYLE.CONTAINER_WHITE}>
      <div>
        <h5 className='mb-1'>Default Dashboard Date Filter</h5>
        <p className='mb-4'>
          Sets the default timeframe view for all dashboards in the system
        </p>

        <div className='mt-6'>
          <div className='mb-8 align-top inline-block'>
            <Controller
              name='defaultDateFilter'
              defaultValue={company.settings.defaultDateFilter}
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Select
                  id='default-date-filter'
                  classes='w-32 mini:w-36 desktop:w-44 full:w-48 wide:w-60'
                  options={DEFAULT_DATE_FILTER_OPTIONS}
                  title={`Filter by ${getDefaultDateFilterLabel(value)}`}
                  onChange={(option) => onChange(option.id)}
                />
              )}
            />
          </div>
        </div>

        <div className='mb-1 block'>
          <Button
            variant='black'
            classes='py-1 whitespace-nowrap h-[39.5px]'
            disabled={isUpdateButtonDisabled}
            onClick={handleSubmit(save)}
          >
            Update
          </Button>
        </div>
      </div>
    </Base>
  );
};

export default DefaultDateFilter;
